import React, { useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";
import {
  Bold, Italic, Strikethrough, Heading1, Heading2, Heading3, List, ListOrdered,
  Quote, Code, Link as LinkIcon, Image as ImageIcon, Undo, Redo, AlignLeft,
  AlignCenter, AlignRight, Minus, Type
} from "lucide-react";
import MediaPicker from "./MediaPicker";

const btn = "inline-flex items-center justify-center w-8 h-8 rounded-md text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))] disabled:opacity-40 disabled:cursor-not-allowed transition-colors";
const btnActive = "bg-[color:var(--brand-navy)] text-white hover:bg-[color:var(--brand-navy)]/90";

export default function RichEditor({ value = "", onChange, placeholder = "Rédigez votre contenu…", minHeight = 360 }) {
  const [picker, setPicker] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { class: "text-[color:var(--brand-sky)] underline underline-offset-2" } }),
      Image.configure({ HTMLAttributes: { class: "rounded-xl my-4 max-w-full h-auto" } }),
      Placeholder.configure({ placeholder }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
    editorProps: {
      attributes: {
        class: "prose prose-sm md:prose-base max-w-none focus:outline-none px-4 py-4 min-h-full",
        "data-testid": "rich-editor-content",
      },
    },
  });

  if (!editor) return <div className="text-sm text-[hsl(var(--muted-foreground))]">Chargement éditeur...</div>;

  const setLink = () => {
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("URL du lien", prev || "https://");
    if (url === null) return;
    if (url === "") { editor.chain().focus().extendMarkRange("link").unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const insertImage = (url) => {
    if (!url) return;
    editor.chain().focus().setImage({ src: url }).run();
    setPicker(false);
  };

  const Group = ({ children }) => <div className="flex items-center gap-0.5 px-1 border-r border-[hsl(var(--border))] last:border-r-0">{children}</div>;

  return (
    <div className="rounded-xl border border-[hsl(var(--border))] bg-white overflow-hidden focus-within:border-[color:var(--brand-navy)] focus-within:ring-2 focus-within:ring-[color:var(--brand-navy)]/10 transition" data-testid="rich-editor">
      {/* Toolbar */}
      <div className="flex items-center flex-wrap gap-0 px-1 py-1 border-b border-[hsl(var(--border))] bg-[hsl(var(--muted))]/40 sticky top-0 z-10">
        <Group>
          <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} className={`${btn} ${editor.isActive("heading", { level: 1 }) ? btnActive : ""}`} title="Titre 1"><Heading1 className="w-4 h-4" /></button>
          <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={`${btn} ${editor.isActive("heading", { level: 2 }) ? btnActive : ""}`} title="Titre 2"><Heading2 className="w-4 h-4" /></button>
          <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} className={`${btn} ${editor.isActive("heading", { level: 3 }) ? btnActive : ""}`} title="Titre 3"><Heading3 className="w-4 h-4" /></button>
          <button type="button" onClick={() => editor.chain().focus().setParagraph().run()} className={`${btn} ${editor.isActive("paragraph") ? btnActive : ""}`} title="Paragraphe"><Type className="w-4 h-4" /></button>
        </Group>
        <Group>
          <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} className={`${btn} ${editor.isActive("bold") ? btnActive : ""}`} title="Gras"><Bold className="w-4 h-4" /></button>
          <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} className={`${btn} ${editor.isActive("italic") ? btnActive : ""}`} title="Italique"><Italic className="w-4 h-4" /></button>
          <button type="button" onClick={() => editor.chain().focus().toggleStrike().run()} className={`${btn} ${editor.isActive("strike") ? btnActive : ""}`} title="Barré"><Strikethrough className="w-4 h-4" /></button>
          <button type="button" onClick={() => editor.chain().focus().toggleCode().run()} className={`${btn} ${editor.isActive("code") ? btnActive : ""}`} title="Code inline"><Code className="w-4 h-4" /></button>
        </Group>
        <Group>
          <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()} className={`${btn} ${editor.isActive("bulletList") ? btnActive : ""}`} title="Liste à puces"><List className="w-4 h-4" /></button>
          <button type="button" onClick={() => editor.chain().focus().toggleOrderedList().run()} className={`${btn} ${editor.isActive("orderedList") ? btnActive : ""}`} title="Liste numérotée"><ListOrdered className="w-4 h-4" /></button>
          <button type="button" onClick={() => editor.chain().focus().toggleBlockquote().run()} className={`${btn} ${editor.isActive("blockquote") ? btnActive : ""}`} title="Citation"><Quote className="w-4 h-4" /></button>
          <button type="button" onClick={() => editor.chain().focus().setHorizontalRule().run()} className={btn} title="Séparateur"><Minus className="w-4 h-4" /></button>
        </Group>
        <Group>
          <button type="button" onClick={() => editor.chain().focus().setTextAlign("left").run()} className={`${btn} ${editor.isActive({ textAlign: "left" }) ? btnActive : ""}`} title="Aligner à gauche"><AlignLeft className="w-4 h-4" /></button>
          <button type="button" onClick={() => editor.chain().focus().setTextAlign("center").run()} className={`${btn} ${editor.isActive({ textAlign: "center" }) ? btnActive : ""}`} title="Centrer"><AlignCenter className="w-4 h-4" /></button>
          <button type="button" onClick={() => editor.chain().focus().setTextAlign("right").run()} className={`${btn} ${editor.isActive({ textAlign: "right" }) ? btnActive : ""}`} title="Aligner à droite"><AlignRight className="w-4 h-4" /></button>
        </Group>
        <Group>
          <button type="button" onClick={setLink} className={`${btn} ${editor.isActive("link") ? btnActive : ""}`} title="Ajouter un lien" data-testid="rich-editor-link"><LinkIcon className="w-4 h-4" /></button>
          <button type="button" onClick={() => setPicker(true)} className={btn} title="Insérer une image (médiathèque)" data-testid="rich-editor-image"><ImageIcon className="w-4 h-4" /></button>
        </Group>
        <Group>
          <button type="button" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} className={btn} title="Annuler"><Undo className="w-4 h-4" /></button>
          <button type="button" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} className={btn} title="Rétablir"><Redo className="w-4 h-4" /></button>
        </Group>
      </div>

      <div className="overflow-y-auto" style={{ minHeight, maxHeight: 600 }}>
        <EditorContent editor={editor} />
      </div>

      <div className="px-3 py-1.5 border-t border-[hsl(var(--border))] bg-[hsl(var(--muted))]/20 text-[10px] text-[hsl(var(--muted-foreground))] flex items-center justify-between">
        <span>{editor.storage.characterCount?.characters?.() ?? editor.getText().length} caractères · {editor.getText().split(/\s+/).filter(Boolean).length} mots</span>
        <span className="font-mono">TipTap · WYSIWYG</span>
      </div>

      {picker && <MediaPicker onPick={insertImage} onClose={() => setPicker(false)} folder="blog" />}
    </div>
  );
}
